import React from 'react';

const COLORS = ['#0077b6', '#d48300', '#14856d', '#7047a3', '#bf4265', '#59742d', '#586574'];

export default function PerformanceChart({id, title, data, series, unit, mode = 'line', emptyText}) {
    const stacked = mode === 'stacked';
    const maxima = data.map(row => stacked
        ? row.values.reduce((sum, value) => sum + (value || 0), 0)
        : Math.max(0, ...row.values));
    const maximum = Math.max(1, ...maxima);
    const ceiling = unit === 'count' ? Math.ceil(maximum / 4) * 4 : maximum * 1.1;
    const width = Math.max(620, data.length * (stacked ? 50 : 35) + 90);
    const left = 60;
    const top = 22;
    const height = 215;
    const plotWidth = width - left - 24;
    const step = plotWidth / Math.max(1, data.length);
    const x = index => left + step * (index + 0.5);
    const y = value => top + height * (1 - value / ceiling);
    const populated = data.some(row => row.values.some(value => value !== null));
    return (
        <section className="performance-chart" aria-labelledby={`${id}-heading`}>
            <h2 id={`${id}-heading`}>{title}</h2>
            <div className="performance-legend">
                {series.map((name, index) => <span key={index}>
                    <i style={{background: COLORS[index % COLORS.length]}} />{name}
                </span>)}
            </div>
            {!populated ? <p>{emptyText}</p> : <div className="performance-chart-scroll">
                <svg viewBox={`0 0 ${width} 370`} style={{minWidth: width}} role="img" aria-labelledby={`${id}-heading`}>
                    {[0, 1, 2, 3, 4].map(tick => {
                        const value = ceiling * tick / 4;
                        return <g key={tick}>
                            <line x1={left} x2={width - 24} y1={y(value)} y2={y(value)} stroke="#e2e8ef" />
                            <text x={left - 9} y={y(value) + 4} textAnchor="end" fontSize="12">
                                {unit === 'count' ? value.toFixed(0) : value.toFixed(2)}
                            </text>
                        </g>;
                    })}
                    {mode === 'line' && series.map((name, index) => <polyline key={index}
                        fill="none" stroke={COLORS[index % COLORS.length]} strokeWidth="2"
                        strokeDasharray={index >= COLORS.length ? '5 3' : undefined}
                        points={data.map((row, i) => `${x(i)},${y(row.values[index])}`).join(' ')} />)}
                    {data.map((row, i) => {
                        let sum = 0;
                        return <g key={row.label}>
                            {row.values.map((value, index) => {
                                if (value === null) return null;
                                const color = COLORS[index % COLORS.length];
                                const tooltip = `${row.label} — ${series[index]}: ${value.toLocaleString(undefined, {maximumFractionDigits: 3})} ${unit}`;
                                const previous = sum;
                                sum += value;
                                const barWidth = Math.min(32, step * 0.75 / (stacked ? 1 : series.length));
                                return mode === 'line'
                                    ? <circle key={index} cx={x(i)} cy={y(value)} r="3" fill={color}><title>{tooltip}</title></circle>
                                    : <rect key={index} fill={color}
                                        x={stacked ? x(i) - barWidth / 2 : x(i) + (index - series.length / 2) * barWidth}
                                        y={y(stacked ? sum : value)} width={barWidth}
                                        height={Math.max(0, y(stacked ? previous : 0) - y(stacked ? sum : value))}>
                                        <title>{tooltip}</title>
                                    </rect>;
                            })}
                            <text transform={`translate(${x(i)},${top + height + 16}) rotate(-45)`}
                                textAnchor="end" fontSize="11">
                                {stacked ? row.label.split(' · ')[0].slice(0, 23) : row.label.replace(/^\d{4}-/, '')}
                            </text>
                        </g>;
                    })}
                    <text x="17" y={top + height / 2} textAnchor="middle"
                        transform={`rotate(-90 17 ${top + height / 2})`} fontSize="12">{unit}</text>
                </svg>
            </div>}
            {populated && <details>
                <summary>View chart data</summary>
                <div className="performance-chart-scroll">
                    <table>
                        <thead><tr><th>Date / sample (HKT)</th>{series.map((name, index) => <th key={index}>{name}</th>)}</tr></thead>
                        <tbody>{data.map(row => <tr key={row.label}>
                            <th scope="row">{row.label}</th>
                            {row.values.map((value, index) => <td key={index}>
                                {value === null ? '—' : value.toLocaleString(undefined, {maximumFractionDigits: 3})}
                            </td>)}
                        </tr>)}</tbody>
                    </table>
                </div>
            </details>}
        </section>
    );
}
